import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CircleAlert, Info, Plug, RefreshCw, Unplug } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import {
  WhatsAppOauthStateResponse,
  WhatsAppSendErrorCategory,
  WhatsAppSendHealthError,
  WhatsAppSetupStatusResponse,
} from '@/Domain/api/WhatsAppPhoneNumbers/WhatsAppPhoneNumbers.api';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import {
  ConnectRecoveryCause,
  ConnectRecoveryPanel,
  ConnectRecoveryReason,
} from '@/Domain/components/Integration/WhatsAppPhoneNumbers/ConnectRecoveryPanel';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import {
  OAuthRedirectCallbackResult,
  useOAuthRedirectCallback,
} from '@/Domain/components/Integration/useIntegrationOAuthCallback';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Card } from '@/Domain/components/Shared/Card/Card';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { WhatsAppIcon } from '@/Domain/components/icons/WhatsApp.icon';
import {
  PostHogEventEnum,
  type WhatsAppSignupResolution,
  type WhatsAppSignupStatus,
} from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

interface PhoneNumberEntry {
  phone_number_id: string;
  display_phone_number: string;
  verified_name: string;
  quality_rating: string;
  is_active: boolean;
}

interface IntegrationInfo {
  whatsapp_id: string;
  account_name: string | null;
  created_timestamp: string;
  webhook_subscribed: boolean;
  admin_assigned: boolean;
  business_id: string | null;
}

interface PhoneNumbersListResponse {
  integration_info: IntegrationInfo;
  phone_numbers: PhoneNumberEntry[];
}

interface ToggleResponse {
  success: boolean;
  updated_entry: PhoneNumberEntry;
}

const QUALITY_BADGE_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'destructive',
};

const QUALITY_DISPLAY_KEY: Record<string, string> = {
  GREEN: 'whatsapp_phone_numbers.card.quality_high',
  YELLOW: 'whatsapp_phone_numbers.card.quality_medium',
  RED: 'whatsapp_phone_numbers.card.quality_low',
};

const QUALITY_TOOLTIP_KEY: Record<string, string> = {
  GREEN: 'whatsapp_phone_numbers.card.quality_high_tooltip',
  YELLOW: 'whatsapp_phone_numbers.card.quality_medium_tooltip',
  RED: 'whatsapp_phone_numbers.card.quality_low_tooltip',
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const META_REDIRECT_URI = 'https://meta.quickchat.ai/login';
// meta_callback.html posts its result with targetOrigin '*'; the listener must
// only trust messages actually coming from the callback host.
const META_CALLBACK_ORIGIN = new URL(META_REDIRECT_URI).origin;
const WHATSAPP_OAUTH_CALLBACK_PARAMS: readonly string[] = ['whatsapp_dialog'];
const FB_APP_ID = '775209690952444';
// Dialog URL version only (runtime messaging pins its own Graph version
// server-side). v18.0 is past its support window; keep this on a live version.
const FB_API_VERSION = 'v25.0';
const FB_CONFIG_ID = '1578381687231165';
const WHATSAPP_DOCS_HREF = 'https://docs.quickchat.ai/channels/whatsapp';
const META_BUSINESS_SETTINGS_HREF =
  'https://business.facebook.com/settings/whatsapp-business-accounts';
// <Trans> placeholders: <0> bolds the scannable lead-in, <1> links out to Meta.
const BOLD_LEAD = <strong className="font-semibold" />;
const META_BUSINESS_SETTINGS_LINK = (
  <a
    href={META_BUSINESS_SETTINGS_HREF}
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium underline"
  />
);

type OAuthLaunchMode = 'popup' | 'redirect';

interface WhatsAppConnectionResultMessage {
  type: 'whatsapp_connection_result';
  status: string;
  title: string;
  message: string;
  reason_code?: string;
}

const isWhatsAppConnectionResultMessage = (
  data: unknown,
): data is WhatsAppConnectionResultMessage => {
  if (typeof data !== 'object' || data === null) return false;
  const result = data as Record<string, unknown>;
  return (
    result.type === 'whatsapp_connection_result' &&
    typeof result.status === 'string' &&
    typeof result.title === 'string' &&
    typeof result.message === 'string' &&
    (result.reason_code === undefined || typeof result.reason_code === 'string')
  );
};

// Meta's own dialog, not our callback: a rejection inside it never reaches our redirect, so this
// is the only channel that can say where an attempt died. Must stay the origin we open the dialog
// on, or the probe goes silently dead, so buildMetaOAuthUrl builds from it too.
const META_DIALOG_ORIGIN = 'https://www.facebook.com';

// Meta's message is free text and the case that motivated this probe ("this phone number is
// already registered...") quotes the merchant's number, so scrub identifiers and cap length:
// enough to classify the rejection, never a copy of customer data in analytics.
const META_ERROR_MESSAGE_MAX = 200;
// event and current_step are documented as short enums, but they arrive in the same untrusted
// payload as the message above, and an unbounded string is a PostHog cardinality problem too.
const META_ENUM_MAX = 64;
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const DIGIT_RUN_PATTERN = /\+?\d[\d\s().-]{5,}\d/g;

const scrubMetaErrorMessage = (message: string): string =>
  message
    .replace(EMAIL_PATTERN, '[email]')
    // A phone number survives any separators Meta formats it with, so match on digit count.
    .replace(DIGIT_RUN_PATTERN, (match) =>
      match.replace(/\D/g, '').length >= 7 ? '[number]' : match,
    )
    .slice(0, META_ERROR_MESSAGE_MAX);

interface MetaDialogSignal {
  meta_event: string;
  meta_current_step: string | null;
  meta_error_message: string | null;
}

const parseMetaDialogSignal = (data: unknown): MetaDialogSignal | null => {
  // Meta sends the payload as a JSON string in some versions of the dialog.
  let payload = data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (typeof payload !== 'object' || payload === null) return null;
  const message = payload as Record<string, unknown>;
  if (message.type !== 'WA_EMBEDDED_SIGNUP') return null;
  const detail = (
    typeof message.data === 'object' && message.data !== null ? message.data : {}
  ) as Record<string, unknown>;
  return {
    meta_event:
      typeof message.event === 'string' ? message.event.slice(0, META_ENUM_MAX) : 'unknown',
    meta_current_step:
      typeof detail.current_step === 'string' ? detail.current_step.slice(0, META_ENUM_MAX) : null,
    meta_error_message:
      typeof detail.error_message === 'string' ? scrubMetaErrorMessage(detail.error_message) : null,
  };
};

const buildMetaOAuthUrl = (
  extraParams: Record<string, string>,
  mode: OAuthLaunchMode,
  state: string,
): string => {
  // URLSearchParams URL-encodes each value once. Hand-concatenating produced
  // a redirect_uri with literal `/`s that Meta's dialog parsed as the bare
  // origin, sending users to https://meta.quickchat.ai/#_=_ instead of /login.
  // The state token gets percent-encoded once here and URL-decoded once by Django.
  const params = new URLSearchParams({
    response_type: 'code',
    ...(mode === 'popup' ? { display: 'popup' } : {}),
    client_id: FB_APP_ID,
    redirect_uri: META_REDIRECT_URI,
    state,
    ...extraParams,
  });

  return `${META_DIALOG_ORIGIN}/${FB_API_VERSION}/dialog/oauth?${params.toString()}`;
};

const openMetaOAuthPopup = (extraParams: Record<string, string>, state: string): Window | null =>
  window.open(
    buildMetaOAuthUrl(extraParams, 'popup', state),
    '_blank',
    'menubar=no,location=no,resizable=no,scrollbars=no,status=no,height=800,width=600',
  );

// The single connect flow — Embedded Signup (a Facebook Login for Business
// config, documented for the manual dialog/oauth flow via config_id) opened as
// a redirect popup, with no FB JS SDK dependency. Meta's wizard covers both
// creating a WABA + phone and picking an existing one, and the backend callback
// runs the full setup in one pass, so a single button suffices.
const WHATSAPP_SIGNUP_PARAMS: Record<string, string> = {
  config_id: FB_CONFIG_ID,
  // Meta ignores response_type=code for business configs unless this is set.
  override_default_response_type: 'true',
  // Force fresh OAuth because Meta can otherwise replay cached scopes without reopening Embedded Signup.
  auth_type: 'reauthorize',
};

const WHATSAPP_PERMISSIONS_PARAMS: Record<string, string> = {
  auth_type: 'rerequest',
  scope: [
    'email',
    'whatsapp_business_management',
    'whatsapp_business_messaging',
    'business_management',
  ].join(','),
};

const openWhatsAppSignupPopup = (state: string): Window | null =>
  openMetaOAuthPopup(WHATSAPP_SIGNUP_PARAMS, state);

// setup_incomplete retry only: plain scoped OAuth. Note our app has STANDARD
// access to `business_management`, so external users can never grant it via any
// consent dialog (PR #4033); the backend deliberately needs no user-side
// business_management (SYSTEM_TOKEN does the privileged calls). This dialog is
// just a lightweight way to re-run the OAuth + setup pipeline.
const openWhatsAppPermissionsPopup = (state: string): Window | null =>
  openMetaOAuthPopup(WHATSAPP_PERMISSIONS_PARAMS, state);

type ConnectDialog = 'embedded_signup' | 'permissions';

const buildWhatsAppRedirectUrl = (dialog: ConnectDialog, state: string): string =>
  buildMetaOAuthUrl(
    dialog === 'embedded_signup' ? WHATSAPP_SIGNUP_PARAMS : WHATSAPP_PERMISSIONS_PARAMS,
    'redirect',
    state,
  );

export const WHATSAPP_PHONE_NUMBERS_SHEET = MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET;

// Alert already indents its content past the icon (`[&>svg~*]:pl-7`); a list margin on top of
// that double-indents inside a 576px sheet, and action rows read as body text rather than actions.
const ALERT_LIST = 'flex list-disc flex-col gap-1 pl-5 text-xs';
const ALERT_ACTIONS = 'flex flex-wrap items-center gap-3 pt-3 !pl-0';

const IssuesBanner = ({ issues }: { issues: string[] }) => {
  if (issues.length === 0) return null;
  return (
    <Alert variant="warning" data-testid="issues-banner">
      <AlertTriangle className="size-4" />
      <AlertTitle>
        {issues.length === 1 ? '1 issue detected' : `${issues.length} issues detected`}
      </AlertTitle>
      <AlertDescription>
        <ul className={ALERT_LIST}>
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
};

// WhatsApp Manager is where the customer fixes number-level settings (display
// name, quality/policy). token_invalid is fixed by reconnecting (the modal's own
// CTA) and generic has no single destination, so neither gets an external link.
const SEND_ERROR_LINKS: Partial<Record<WhatsAppSendErrorCategory, string>> = {
  display_name_approval: 'https://business.facebook.com/wa/manage/',
  number_quality: 'https://business.facebook.com/wa/manage/',
};

const SendErrorBanner = ({ sendError }: { sendError: WhatsAppSendHealthError }) => {
  const { t } = useTranslation(['integrations']);
  const link = SEND_ERROR_LINKS[sendError.category];
  return (
    <Alert variant="destructive" data-testid="whatsapp-send-error-banner">
      <CircleAlert className="size-4" />
      <AlertTitle>{t('whatsapp_phone_numbers.send_error.title')}</AlertTitle>
      <AlertDescription>
        <p>{t(`whatsapp_phone_numbers.send_error.${sendError.category}`)}</p>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-xs font-medium underline"
          >
            {t('whatsapp_phone_numbers.send_error.learn_more')}
          </a>
        )}
      </AlertDescription>
    </Alert>
  );
};

// How long the in-progress banner can stay up before we give up waiting for
// `integrationStatus === 'connected'`. setup_whatsapp_account observed
// taking ~22s in Production (Datadog trace c7953edcb53c14cc57251c2d360776be);
// 60s gives a comfortable margin without parking a broken state on screen.
const SETUP_IN_PROGRESS_TIMEOUT_MS = 60_000;
// Status query refetch cadence while a setup attempt is in flight. Belt-and-
// suspenders for the COOP-may-opaque-popup.closed and missing-postMessage
// cases; the user gets a sheet update within ~3s of the backend writing the
// row even if both existing refresh triggers fail silently.
const SETUP_IN_PROGRESS_POLL_INTERVAL_MS = 3_000;
// Stop popup-tied polling without closing a third-party window that may still be active.
const OAUTH_POPUP_POLL_TIMEOUT_MS = 120_000;
const RECOVERY_CONFIRMATION_GRACE_MS = 10_000;
const RECOVERY_CONFIRMATION_POLL_TIMEOUT_MS = 18_000;
const OAUTH_STATE_STALE_TIME_MS = 5 * 60_000;
const OAUTH_STATE_ERROR_RETRY_BASE_INTERVAL_MS = 3_000;
const OAUTH_STATE_ERROR_RETRY_MAX_INTERVAL_MS = 30_000;
const OAUTH_STATE_ERROR_RETRY_JITTER_MS = 1_000;
const OAUTH_STATE_ERROR_REFETCH_LIMIT = 4;
const RETRYABLE_OAUTH_STATE_CLIENT_ERRORS = new Set([404, 408, 425, 429]);
// Widening the status enum must not widen `integration_connect_failed.reason` with it.
const STATUS_FAILURE_REASONS: Partial<Record<WhatsAppSetupStatusResponse['status'], string>> = {
  setup_incomplete: 'setup_incomplete',
  waba_inaccessible: 'waba_inaccessible',
};

const getOAuthStateErrorResponse = (
  error: unknown,
): { status?: unknown; headers?: unknown } | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
  return (error as { response?: { status?: unknown; headers?: unknown } }).response;
};

const isNonRetryableOAuthStateError = (error: unknown): boolean => {
  const status = getOAuthStateErrorResponse(error)?.status;
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_OAUTH_STATE_CLIENT_ERRORS.has(status)
  );
};

const getOAuthStateRetryAfterMs = (error: unknown): number => {
  const response = getOAuthStateErrorResponse(error);
  if (response?.status !== 429 || typeof response.headers !== 'object' || !response.headers)
    return 0;
  const headers = response.headers as {
    get?: (name: string) => unknown;
    'retry-after'?: unknown;
  };
  const retryAfter =
    typeof headers.get === 'function' ? headers.get('retry-after') : headers['retry-after'];
  const retryAfterSeconds = Number(retryAfter);
  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? retryAfterSeconds * 1_000
    : 0;
};

const oauthStateRefetchInterval = (
  status: string,
  error: unknown,
  errorUpdateCount: number,
): number | false => {
  if (isNonRetryableOAuthStateError(error)) return false;
  if (status !== 'error') return OAUTH_STATE_STALE_TIME_MS;
  if (errorUpdateCount >= OAUTH_STATE_ERROR_REFETCH_LIMIT) return false;
  const exponentialDelay = Math.min(
    OAUTH_STATE_ERROR_RETRY_BASE_INTERVAL_MS * 2 ** Math.max(0, errorUpdateCount - 1),
    OAUTH_STATE_ERROR_RETRY_MAX_INTERVAL_MS,
  );
  return Math.max(
    exponentialDelay + Math.floor(Math.random() * OAUTH_STATE_ERROR_RETRY_JITTER_MS),
    getOAuthStateRetryAfterMs(error),
  );
};

interface SetupInProgressFlag {
  since: number | null;
}

interface ConnectRecovery {
  reason: ConnectRecoveryReason;
  dialog: ConnectDialog;
  resolution: WhatsAppSignupResolution;
}

interface SignupResultContext {
  dialog: ConnectDialog | null;
  startedAt: number | null;
  priorStatus?: WhatsAppSetupStatusResponse['status'] | null;
}

interface OAuthPopup {
  win: Window;
  dialog: ConnectDialog;
  openedAt: number;
  initialStatus: WhatsAppSetupStatusResponse['status'];
}

// localStorage key for the in-progress flag. Survives full page reload —
// without persistence, a user who refreshes during the 20-30s setup window
// loses the in-progress banner and sees an unchanged "not started" view
// (Dominik's "Step 1 changed nothing even after refresh" symptom).
const SETUP_IN_PROGRESS_LS_KEY = 'whatsapp_setup_in_progress';

const readSetupInProgressFromStorage = (): SetupInProgressFlag => {
  try {
    const raw = window.localStorage.getItem(SETUP_IN_PROGRESS_LS_KEY);
    if (!raw) return { since: null };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || !('since' in parsed)) {
      return { since: null };
    }
    const since = (parsed as { since: unknown }).since;
    if (typeof since !== 'number') return { since: null };
    // Auto-expire stale flags so a tab that crashed mid-setup doesn't park
    // the banner forever on the next visit. The 60s window mirrors the
    // backend's SETUP_IN_PROGRESS_WINDOW.
    if (Date.now() - since > SETUP_IN_PROGRESS_TIMEOUT_MS) return { since: null };
    return { since };
  } catch {
    return { since: null };
  }
};

const writeSetupInProgressToStorage = (flag: SetupInProgressFlag): void => {
  try {
    if (flag.since === null) {
      window.localStorage.removeItem(SETUP_IN_PROGRESS_LS_KEY);
    } else {
      window.localStorage.setItem(SETUP_IN_PROGRESS_LS_KEY, JSON.stringify(flag));
    }
  } catch {
    // localStorage can throw in private-browsing modes; the in-memory React
    // Query cache still works, we just lose persistence across reload.
  }
};

export const WhatsAppPhoneNumbersManager = () => {
  const { t } = useTranslation(['integrations']);
  const open = useModalStore(
    useCallback((state) => state.isOpen(WHATSAPP_PHONE_NUMBERS_SHEET), []),
  );
  const closeModal = useModalStore((state) => state.closeModal);
  const toggleModal = useModalStore((state) => state.toggleModal);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  // Connect and permissions-recovery popups share this machinery; `dialog` tags analytics.
  const [oauthPopup, setOauthPopup] = useState<OAuthPopup | null>(null);
  const [oauthPopupDeadline, setOauthPopupDeadline] = useState<number | null>(null);
  const [pendingRecovery, setPendingRecovery] = useState<{
    recovery: ConnectRecovery;
    initialStatus: WhatsAppSetupStatusResponse['status'];
    initialStatusUpdateCount: number;
    requiredStatusUpdateCount: number;
    eligibleAt: number;
    confirmation: number;
  } | null>(null);
  const [connectRecovery, setConnectRecovery] = useState<ConnectRecovery | null>(null);
  const [oauthLaunchLocked, setOauthLaunchLocked] = useState(false);
  const recoveryConfirmationRef = useRef(0);
  const oauthPostMessageReceivedRef = useRef(false);
  const oauthNoResultHandledRef = useRef(false);
  const activePopupRef = useRef<OAuthPopup | null>(null);
  const timedOutPopupRef = useRef<OAuthPopup | null>(null);
  const untrackedPopupSetupRef = useRef<SignupResultContext | null>(null);
  const oauthLaunchLockedRef = useRef(false);
  const redirectSuccessPendingRef = useRef(false);
  const redirectSuccessSettledRef = useRef(false);
  const redirectSuccessDialogRef = useRef<ConnectDialog | null>(null);
  const setupConfirmationStatusUpdateCountRef = useRef<number | null>(null);
  const cachedIntegrationStatus = queryClient.getQueryData<WhatsAppSetupStatusResponse>([
    QueryKey.whatsappPhoneNumbers.status,
  ])?.status;
  const popupOauthDialog =
    connectRecovery?.dialog ??
    (cachedIntegrationStatus === 'setup_incomplete' ? 'permissions' : 'embedded_signup');
  const {
    data: popupOauthState,
    isFetching: popupOauthStateFetching,
    isError: popupOauthStateError,
  } = useQuery<WhatsAppOauthStateResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.oauthState, 'popup', popupOauthDialog],
    enabled: open,
    staleTime: OAUTH_STATE_STALE_TIME_MS,
    refetchInterval: (query) =>
      oauthStateRefetchInterval(
        query.state.status,
        query.state.error,
        query.state.errorUpdateCount,
      ),
  });
  const {
    data: redirectOauthState,
    isFetching: redirectOauthStateFetching,
    isError: redirectOauthStateError,
  } = useQuery<WhatsAppOauthStateResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.oauthState, 'redirect', connectRecovery?.dialog],
    enabled: open && connectRecovery !== null,
    staleTime: OAUTH_STATE_STALE_TIME_MS,
    refetchInterval: (query) =>
      oauthStateRefetchInterval(
        query.state.status,
        query.state.error,
        query.state.errorUpdateCount,
      ),
  });
  const popupOauthUnavailable =
    popupOauthStateFetching || popupOauthStateError || popupOauthState === undefined;
  const redirectOauthUnavailable =
    redirectOauthStateFetching || redirectOauthStateError || redirectOauthState === undefined;

  // Client cache slot — backed by localStorage so the in-progress banner
  // survives a full page reload during the 20-30s setup window. A successful
  // OAuth callback flips this to a timestamp; the sheet renders the
  // in-progress banner and disables the connect actions until either
  // (a) `integrationStatus` transitions to 'connected', or (b) the local
  // SETUP_IN_PROGRESS_TIMEOUT_MS elapses, or (c) the backend status query
  // confirms `setup_in_progress` is no longer the right status.
  const { data: setupInProgress } = useQuery<SetupInProgressFlag>({
    queryKey: [QueryKey.whatsappPhoneNumbers.setupInProgress],
    queryFn: () => Promise.resolve(readSetupInProgressFromStorage()),
    staleTime: Infinity,
    initialData: readSetupInProgressFromStorage,
  });
  const setupInProgressSince = setupInProgress?.since ?? null;
  const integrationStatusFromBackend = useQuery<WhatsAppSetupStatusResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.status],
    enabled: false, // just a read of the cache; the main query below is what fetches
  }).data?.status;
  // Show the in-progress banner when EITHER the local flag is set (fresh
  // click feedback before any backend roundtrip) OR the backend status
  // reports setup_in_progress (covers the page-reload case where the local
  // flag may have been cleared but the row was created < 60s ago on the
  // server side).
  const isSetupInProgress =
    setupInProgressSince !== null || integrationStatusFromBackend === 'setup_in_progress';
  const popupLaunchDisabled =
    popupOauthUnavailable ||
    oauthLaunchLocked ||
    oauthPopup !== null ||
    pendingRecovery !== null ||
    isSetupInProgress;
  const redirectLaunchDisabled =
    redirectOauthUnavailable ||
    oauthLaunchLocked ||
    oauthPopup !== null ||
    pendingRecovery !== null ||
    isSetupInProgress;

  const markSetupInProgress = useCallback(() => {
    const flag = { since: Date.now() };
    writeSetupInProgressToStorage(flag);
    queryClient.setQueryData<SetupInProgressFlag>(
      [QueryKey.whatsappPhoneNumbers.setupInProgress],
      flag,
    );
  }, [queryClient]);

  const clearSetupInProgress = useCallback(() => {
    const flag: SetupInProgressFlag = { since: null };
    writeSetupInProgressToStorage(flag);
    queryClient.setQueryData<SetupInProgressFlag>(
      [QueryKey.whatsappPhoneNumbers.setupInProgress],
      flag,
    );
  }, [queryClient]);

  const closeTimedOutPopup = useCallback(() => {
    const popup = timedOutPopupRef.current;
    timedOutPopupRef.current = null;
    if (!popup) return;
    try {
      if (typeof popup.win.close === 'function') popup.win.close();
    } catch {
      // COOP can make the retained WindowProxy opaque.
    }
  }, []);

  const {
    data: statusData,
    isLoading: statusLoading,
    isFetching: statusFetching,
    dataUpdatedAt: statusUpdatedAt,
  } = useQuery<WhatsAppSetupStatusResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.status],
    enabled: open,
  });

  const integrationStatus = statusData?.status ?? 'not_started';

  const { data, isLoading } = useQuery<PhoneNumbersListResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.list],
    enabled: open && integrationStatus === 'connected',
  });

  const toggleMutation = useMutation<
    ToggleResponse,
    Error,
    { phone_number_id: string; is_active: boolean }
  >({
    mutationKey: [QueryKey.whatsappPhoneNumbers.toggle],
  });

  const deleteMutation = useMutation<{ success: boolean }, Error, void>({
    mutationKey: [QueryKey.whatsappPhoneNumbers.delete],
    onSuccess: () => {
      // Only a real disconnect is churn. The shared delete mutation also backs the
      // "start over" action for never-connected setups (setup_incomplete /
      // waba_inaccessible), which must not count as a disconnect.
      if (integrationStatus === 'connected') {
        capture(PostHogEventEnum.WhatsappDisconnected);
      }
      // Disconnect flips status to not-connected, which disables the phone-numbers
      // query (enabled: integrationStatus === 'connected'). A disabled query keeps
      // its last data, so invalidating it never refetched and the enabled badge +
      // phone number lingered until a manual refresh — drop the cache outright.
      // Refetch status so the sheet repaints into the not-connected setup state.
      queryClient.removeQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.list] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.status] });
      clearSetupInProgress();
      // A retained popup still holds a live Meta wizard; finishing it would undo this removal.
      closeTimedOutPopup();
      toast.success(t('whatsapp_phone_numbers.disconnect.success'));
      setDisconnectDialogOpen(false);
    },
    onError: () => {
      toast.error(t('whatsapp_phone_numbers.disconnect.error'));
    },
  });

  const phoneNumbers = data?.phone_numbers ?? [];
  const integrationInfo = data?.integration_info;

  // WABA id, connected-since and webhook/admin status arrive with the phone-numbers fetch.
  const integrationInfoLoading = integrationStatus === 'connected' && isLoading;
  const isPreloading = statusLoading || integrationInfoLoading;

  const handleToggle = (entry: PhoneNumberEntry, checked: boolean) => {
    toggleMutation.mutate(
      {
        phone_number_id: entry.phone_number_id,
        is_active: checked,
      },
      {
        onSuccess: () => {
          capture(PostHogEventEnum.WhatsappPhoneNumberToggled, { is_active: checked });
          void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.list] });
          void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.status] });
          if (checked) {
            toast.success(
              t('whatsapp_phone_numbers.toast.activated', { number: entry.display_phone_number }),
            );
          } else {
            toast.success(
              t('whatsapp_phone_numbers.toast.deactivated', {
                number: entry.display_phone_number,
              }),
            );
          }
        },
        onError: () => {
          toast.error(t('whatsapp_phone_numbers.toast.error'));
        },
      },
    );
  };

  const refreshSetupStatus = useCallback(() => {
    void queryClient.invalidateQueries(
      { queryKey: [QueryKey.whatsappPhoneNumbers.status] },
      { cancelRefetch: false },
    );
  }, [queryClient]);

  const handleRefreshStatus = useCallback(() => {
    refreshSetupStatus();
    void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.list] });
    if (popupOauthStateError) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.whatsappPhoneNumbers.oauthState, 'popup', popupOauthDialog],
        exact: true,
      });
    }
    if (redirectOauthStateError && connectRecovery !== null) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.whatsappPhoneNumbers.oauthState, 'redirect', connectRecovery.dialog],
        exact: true,
      });
    }
  }, [
    connectRecovery,
    popupOauthDialog,
    popupOauthStateError,
    queryClient,
    redirectOauthStateError,
    refreshSetupStatus,
  ]);

  const clearConnectRecovery = useCallback(() => {
    recoveryConfirmationRef.current += 1;
    setPendingRecovery(null);
    setConnectRecovery(null);
  }, []);

  const lockOauthLaunch = useCallback((): boolean => {
    if (oauthLaunchLockedRef.current) return false;
    oauthLaunchLockedRef.current = true;
    setOauthLaunchLocked(true);
    return true;
  }, []);

  const unlockOauthLaunch = useCallback(() => {
    oauthLaunchLockedRef.current = false;
    setOauthLaunchLocked(false);
  }, []);

  const confirmConnectRecovery = useCallback(
    (
      recovery: ConnectRecovery,
      initialStatus: WhatsAppSetupStatusResponse['status'] = integrationStatus,
    ) => {
      const confirmation = ++recoveryConfirmationRef.current;
      void queryClient
        .cancelQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.status] })
        .then(() => {
          if (confirmation !== recoveryConfirmationRef.current) return;
          const initialStatusUpdateCount =
            queryClient.getQueryState([QueryKey.whatsappPhoneNumbers.status])?.dataUpdateCount ?? 0;
          setPendingRecovery({
            recovery,
            initialStatus,
            initialStatusUpdateCount,
            requiredStatusUpdateCount: initialStatusUpdateCount + 2,
            eligibleAt: Date.now() + RECOVERY_CONFIRMATION_GRACE_MS,
            confirmation,
          });
          refreshSetupStatus();
        });
    },
    [integrationStatus, queryClient, refreshSetupStatus],
  );

  // Reason-code → user-facing Toast. Each backend setup failure surfaces a
  // specific reason_code via the OAuth callback HTML; we map it to copy
  // that tells the user what to do next, instead of the generic
  // "transient — please retry" Toast that hid every distinct failure
  // mode into one bucket (the bug that compounded PR #4015 → #4033).
  const dispatchToastForReasonCode = useCallback(
    (reasonCode: string | null | undefined) => {
      if (!reasonCode) return;
      switch (reasonCode) {
        case 'missing_business_management_scope':
          toast.error(t('whatsapp_phone_numbers.toast.missing_business_management.title'), {
            description: t('whatsapp_phone_numbers.toast.missing_business_management.description'),
            duration: 12000,
          });
          break;
        case 'admin_assign_failed_not_business_admin':
          toast.error(t('whatsapp_phone_numbers.toast.not_business_admin.title'), {
            description: t('whatsapp_phone_numbers.toast.not_business_admin.description'),
            duration: 12000,
          });
          break;
        case 'webhook_subscribe_failed':
          toast.error(t('whatsapp_phone_numbers.toast.webhook_subscribe_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.webhook_subscribe_failed.description'),
            duration: 12000,
          });
          break;
        case 'phone_register_failed':
          toast.error(t('whatsapp_phone_numbers.toast.phone_register_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.phone_register_failed.description'),
            duration: 12000,
          });
          break;
        // A stale bundle keeps minting states the deployed callback rejects, so
        // retrying loops until the page picks up new assets.
        case 'invalid_oauth_state':
          toast.error(t('whatsapp_phone_numbers.toast.oauth_state_expired.title'), {
            description: t('whatsapp_phone_numbers.toast.oauth_state_expired.description'),
            action: {
              label: t('whatsapp_phone_numbers.toast.oauth_state_expired.action'),
              onClick: () => window.location.reload(),
            },
            duration: 15000,
          });
          break;
        // Deterministic, not transient: retrying under the same role fails identically.
        case 'oauth_state_not_authorized':
          toast.error(t('whatsapp_phone_numbers.toast.oauth_state_not_authorized.title'), {
            description: t('whatsapp_phone_numbers.toast.oauth_state_not_authorized.description'),
            duration: 15000,
          });
          break;
        case 'oauth_code_already_used':
        case 'oauth_exchange_failed':
        case 'oauth_error':
        case 'malformed_state_or_code':
        case 'oauth_state_replayed':
        case 'oauth_state_unavailable':
          toast.error(t('whatsapp_phone_numbers.toast.oauth_exchange_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.oauth_exchange_failed.description'),
            duration: 10000,
          });
          break;
        case 'no_whatsapp_business_id':
          toast.error(t('whatsapp_phone_numbers.toast.no_whatsapp_business_id.title'), {
            description: t('whatsapp_phone_numbers.toast.no_whatsapp_business_id.description'),
            duration: 15000,
          });
          break;
        case 'no_phone_number_on_waba':
          toast.error(t('whatsapp_phone_numbers.toast.no_phone_number_on_waba.title'), {
            description: t('whatsapp_phone_numbers.toast.no_phone_number_on_waba.description'),
            duration: 20000,
          });
          break;
        case 'multiple_businesses_selected':
          toast.error(t('whatsapp_phone_numbers.toast.multiple_businesses_selected.title'), {
            description: t('whatsapp_phone_numbers.toast.multiple_businesses_selected.description'),
            duration: 15000,
          });
          break;
        case 'save_integrity_error':
          toast.error(t('whatsapp_phone_numbers.toast.save_integrity_error.title'), {
            description: t('whatsapp_phone_numbers.toast.save_integrity_error.description'),
            duration: 15000,
          });
          break;
        case 'waba_not_shared_with_partner':
          toast.error(t('whatsapp_phone_numbers.toast.waba_not_shared_with_partner.title'), {
            description: t('whatsapp_phone_numbers.toast.waba_not_shared_with_partner.description'),
            duration: 20000,
          });
          break;
        case 'setup_failed':
          toast.error(t('whatsapp_phone_numbers.toast.setup_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.setup_failed.description'),
            duration: 12000,
          });
          break;
        default:
          // Unmapped reason_code — surface the drift instead of silently bucketing it into the generic toast.
          console.error('[WhatsApp] Unmapped connect-failure reason_code:', reasonCode);
          toast.error(t('whatsapp_phone_numbers.toast.generic.title'), {
            description: t('whatsapp_phone_numbers.toast.generic.description'),
            duration: 12000,
          });
          break;
      }
    },
    [t],
  );

  const connectAttemptRef = useRef<{
    dialog: ConnectDialog;
    startedAt: number;
    priorStatus: WhatsAppSetupStatusResponse['status'];
  } | null>(null);
  // Last thing Meta's dialog told us during the current attempt, if anything.
  const metaDialogSignalRef = useRef<MetaDialogSignal | null>(null);
  const emitSignupResult = useCallback(
    (
      status: WhatsAppSignupStatus,
      reason: string | null,
      resolution: WhatsAppSignupResolution,
      context: SignupResultContext,
    ) => {
      capture(PostHogEventEnum.WhatsappSignupResult, {
        status,
        reason,
        resolution,
        dialog: context.dialog,
        prior_status: context.priorStatus ?? null,
        elapsed_ms: context.startedAt === null ? null : Date.now() - context.startedAt,
        // Null whenever the probe heard nothing, which does not distinguish a silent dialog from an attempt that died first.
        meta_event: null,
        meta_current_step: null,
        meta_error_message: null,
        ...metaDialogSignalRef.current,
      });
    },
    [capture],
  );

  const emitSignupOutcome = useCallback(
    (
      status: WhatsAppSignupStatus,
      reason: string | null,
      resolution: WhatsAppSignupResolution,
      context: SignupResultContext,
    ) => {
      emitSignupResult(status, reason, resolution, context);
      if (status === 'connected') {
        trackIntegrationConnectSucceeded(
          capture,
          'whatsapp',
          context.priorStatus ? { prior_status: context.priorStatus } : undefined,
        );
      } else if (status === 'failed') {
        trackIntegrationConnectFailed(capture, 'whatsapp', reason ?? 'unknown');
      }
    },
    [capture, emitSignupResult],
  );

  const finishConnectAttempt = useCallback(
    (
      status: WhatsAppSignupStatus,
      reason: string | null,
      resolution: WhatsAppSignupResolution,
      priorStatus?: WhatsAppSetupStatusResponse['status'] | null,
    ): boolean => {
      const attempt = connectAttemptRef.current;
      if (attempt === null) return false;
      connectAttemptRef.current = null;
      const resolvedPriorStatus = priorStatus ?? attempt.priorStatus;
      emitSignupOutcome(status, reason, resolution, {
        dialog: attempt.dialog,
        startedAt: attempt.startedAt,
        priorStatus: resolvedPriorStatus,
      });
      return true;
    },
    [emitSignupOutcome],
  );

  // An attempt can outlive its page (bfcache restore); reusing it would bill the replacement with the previous clock and dialog.
  const markConnectStarted = useCallback(
    (dialog: ConnectDialog) => {
      finishConnectAttempt('abandoned', 'superseded', 'superseded');
      connectAttemptRef.current = { dialog, startedAt: Date.now(), priorStatus: integrationStatus };
      metaDialogSignalRef.current = null;
      trackIntegrationConnectStarted(capture, 'whatsapp', { prior_status: integrationStatus });
    },
    [capture, finishConnectAttempt, integrationStatus],
  );

  const finishUnconfirmedAttempt = useCallback(
    (recovery: ConnectRecovery) => {
      finishConnectAttempt(
        recovery.resolution === 'popup_deadline' ? 'timed_out' : 'abandoned',
        'status_not_confirmed',
        recovery.resolution,
      );
    },
    [finishConnectAttempt],
  );

  // Don't mark in-progress on click — the Meta wizard can take 30-60s;
  // setupInProgress is set in the postMessage('success') handler (or via the
  // backend status polling once the row exists).
  const launchOAuthPopup = useCallback(
    (dialog: ConnectDialog) => {
      const oauthState = popupOauthState?.state;
      if (!oauthState || popupOauthDialog !== dialog || !lockOauthLaunch()) return;
      closeTimedOutPopup();
      redirectSuccessPendingRef.current = false;
      redirectSuccessSettledRef.current = false;
      redirectSuccessDialogRef.current = null;
      untrackedPopupSetupRef.current = null;
      setupConfirmationStatusUpdateCountRef.current = null;
      capture(PostHogEventEnum.WhatsappSignupInitiated, { dialog, mode: 'popup' });
      markConnectStarted(dialog);
      oauthPostMessageReceivedRef.current = false;
      oauthNoResultHandledRef.current = false;
      clearConnectRecovery();
      const popup =
        dialog === 'embedded_signup'
          ? openWhatsAppSignupPopup(oauthState)
          : openWhatsAppPermissionsPopup(oauthState);
      queryClient.removeQueries({
        queryKey: [QueryKey.whatsappPhoneNumbers.oauthState, 'popup', dialog],
        exact: true,
      });
      if (!popup) {
        oauthNoResultHandledRef.current = true;
        // A blocked popup is terminal, so expose the same-tab fallback immediately.
        finishConnectAttempt('failed', 'popup_blocked', 'popup_blocked');
        toast.error(t('whatsapp_phone_numbers.toast.popup_blocked.title'), {
          description: t('whatsapp_phone_numbers.toast.popup_blocked.description'),
          duration: 12000,
        });
        setConnectRecovery({ reason: 'popup_blocked', dialog, resolution: 'popup_blocked' });
        unlockOauthLaunch();
        return;
      }
      const popupState = {
        win: popup,
        dialog,
        openedAt: Date.now(),
        initialStatus: integrationStatus,
      };
      activePopupRef.current = popupState;
      setOauthPopup(popupState);
      setOauthPopupDeadline(Date.now() + OAUTH_POPUP_POLL_TIMEOUT_MS);
    },
    [
      popupOauthState,
      popupOauthDialog,
      lockOauthLaunch,
      closeTimedOutPopup,
      capture,
      markConnectStarted,
      clearConnectRecovery,
      integrationStatus,
      queryClient,
      finishConnectAttempt,
      t,
      unlockOauthLaunch,
    ],
  );

  const handleContinueInTabClick = useCallback(() => {
    const oauthState = redirectOauthState?.state;
    const dialog = connectRecovery?.dialog;
    if (!oauthState || !dialog || !lockOauthLaunch()) return;
    closeTimedOutPopup();
    redirectSuccessPendingRef.current = false;
    redirectSuccessSettledRef.current = false;
    redirectSuccessDialogRef.current = null;
    untrackedPopupSetupRef.current = null;
    setupConfirmationStatusUpdateCountRef.current = null;
    capture(PostHogEventEnum.WhatsappSignupInitiated, {
      dialog,
      mode: 'redirect',
    });
    markConnectStarted(dialog);
    queryClient.removeQueries({
      queryKey: [QueryKey.whatsappPhoneNumbers.oauthState, 'redirect', dialog],
      exact: true,
    });
    window.location.href = buildWhatsAppRedirectUrl(dialog, oauthState);
  }, [
    redirectOauthState,
    connectRecovery,
    lockOauthLaunch,
    closeTimedOutPopup,
    capture,
    markConnectStarted,
    queryClient,
  ]);

  // The same-tab launch navigates away still holding the launch lock, so Back
  // from Meta restores a bfcached page with every connect action disabled.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || oauthPopup !== null) return;
      unlockOauthLaunch();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [oauthPopup, unlockOauthLaunch]);

  // Last-resort escape: the panel is the only connect affordance while it is
  // shown, so it must be able to hand the sheet back in any state.
  const handleDismissRecovery = useCallback(() => {
    finishConnectAttempt('abandoned', 'dismissed', 'recovery_dismissed');
    clearConnectRecovery();
    unlockOauthLaunch();
    handleRefreshStatus();
  }, [finishConnectAttempt, clearConnectRecovery, unlockOauthLaunch, handleRefreshStatus]);

  // Reporting is not settling: the attempt keeps whatever terminal state it already had.
  const handleReportRecoveryCause = useCallback(
    (cause: ConnectRecoveryCause) => {
      capture(PostHogEventEnum.WhatsappConnectReasonReported, {
        cause,
        reason: connectRecovery?.reason ?? null,
        dialog: connectRecovery?.dialog ?? null,
        resolution: connectRecovery?.resolution ?? null,
      });
    },
    [capture, connectRecovery],
  );

  const handleConnectClick = useCallback(
    () => launchOAuthPopup('embedded_signup'),
    [launchOAuthPopup],
  );
  const handleReconnectClick = useCallback(
    () => launchOAuthPopup('permissions'),
    [launchOAuthPopup],
  );

  useEffect(() => {
    if (!isSetupInProgress && pendingRecovery === null) return;
    const interval = setInterval(refreshSetupStatus, SETUP_IN_PROGRESS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isSetupInProgress, pendingRecovery, refreshSetupStatus]);

  // Auto-clear the in-progress flag after a hard timeout so a backend that
  // silently dies doesn't park the banner on screen forever.
  useEffect(() => {
    if (!setupInProgressSince) return;
    const elapsed = Date.now() - setupInProgressSince;
    const remaining = Math.max(0, SETUP_IN_PROGRESS_TIMEOUT_MS - elapsed);
    const timeout = setTimeout(() => {
      const redirectPending = redirectSuccessPendingRef.current;
      const redirectSettled = redirectSuccessSettledRef.current;
      const redirectDialog = redirectSuccessDialogRef.current;
      const untrackedPopupSetup = untrackedPopupSetupRef.current;
      redirectSuccessPendingRef.current = false;
      redirectSuccessSettledRef.current = false;
      redirectSuccessDialogRef.current = null;
      untrackedPopupSetupRef.current = null;
      setupConfirmationStatusUpdateCountRef.current = null;
      if (redirectPending && !redirectSettled) {
        if (!finishConnectAttempt('timed_out', 'status_not_confirmed', 'status_timeout')) {
          emitSignupOutcome('timed_out', 'status_not_confirmed', 'status_timeout', {
            dialog: redirectDialog,
            startedAt: null,
          });
        }
      }
      if (untrackedPopupSetup) {
        emitSignupOutcome(
          'timed_out',
          'status_not_confirmed',
          'status_timeout',
          untrackedPopupSetup,
        );
      }
      if (!redirectPending) {
        finishConnectAttempt('timed_out', 'status_not_confirmed', 'status_timeout');
      }
      clearSetupInProgress();
    }, remaining);
    return () => clearTimeout(timeout);
  }, [setupInProgressSince, clearSetupInProgress, emitSignupOutcome, finishConnectAttempt]);

  // Clear the in-progress flag once the status query confirms connected —
  // the integration is live, the user no longer needs the loading banner.
  useEffect(() => {
    if (
      integrationStatus === 'connected' &&
      isSetupInProgress &&
      setupConfirmationStatusUpdateCountRef.current === null
    ) {
      clearSetupInProgress();
    }
  }, [integrationStatus, isSetupInProgress, clearSetupInProgress]);

  useEffect(() => {
    if (pendingRecovery === null) return;
    const { confirmation, recovery } = pendingRecovery;
    const expiryTimeout = setTimeout(() => {
      if (recoveryConfirmationRef.current !== confirmation) return;
      finishUnconfirmedAttempt(recovery);
      setConnectRecovery({
        reason: 'status_not_confirmed',
        dialog: recovery.dialog,
        resolution: recovery.resolution,
      });
      setPendingRecovery(null);
      unlockOauthLaunch();
    }, RECOVERY_CONFIRMATION_POLL_TIMEOUT_MS);
    return () => clearTimeout(expiryTimeout);
  }, [pendingRecovery, finishUnconfirmedAttempt, unlockOauthLaunch]);

  useEffect(() => {
    if (pendingRecovery === null || statusFetching) return;
    const statusUpdateCount =
      queryClient.getQueryState([QueryKey.whatsappPhoneNumbers.status])?.dataUpdateCount ?? 0;
    if (statusUpdateCount <= pendingRecovery.initialStatusUpdateCount) return;
    const connectionConfirmed =
      integrationStatus === 'connected' && pendingRecovery.initialStatus !== 'connected';
    if (connectionConfirmed || isSetupInProgress) {
      setPendingRecovery(null);
      unlockOauthLaunch();
      return;
    }
    const reachedNewTerminalFailure =
      (integrationStatus === 'setup_incomplete' || integrationStatus === 'waba_inaccessible') &&
      integrationStatus !== pendingRecovery.initialStatus;
    if (reachedNewTerminalFailure) {
      setPendingRecovery(null);
      unlockOauthLaunch();
      finishConnectAttempt(
        'failed',
        STATUS_FAILURE_REASONS[integrationStatus] ?? 'backend_error',
        pendingRecovery.recovery.resolution,
      );
      return;
    }
    if (pendingRecovery.initialStatus === 'connected' && integrationStatus === 'connected') {
      if (
        statusUpdateCount < pendingRecovery.requiredStatusUpdateCount ||
        Date.now() < pendingRecovery.eligibleAt
      ) {
        return;
      }
      finishUnconfirmedAttempt(pendingRecovery.recovery);
      setConnectRecovery(pendingRecovery.recovery);
      setPendingRecovery(null);
      unlockOauthLaunch();
      return;
    }
    const canRetryConnect =
      integrationStatus === 'not_started' ||
      integrationStatus === 'incomplete' ||
      integrationStatus === 'setup_incomplete' ||
      integrationStatus === 'waba_inaccessible';
    if (!canRetryConnect) {
      setPendingRecovery(null);
      unlockOauthLaunch();
      return;
    }
    if (
      statusUpdateCount < pendingRecovery.requiredStatusUpdateCount ||
      Date.now() < pendingRecovery.eligibleAt
    ) {
      return;
    }
    finishUnconfirmedAttempt(pendingRecovery.recovery);
    setConnectRecovery(pendingRecovery.recovery);
    setPendingRecovery(null);
    unlockOauthLaunch();
  }, [
    pendingRecovery,
    statusFetching,
    isSetupInProgress,
    integrationStatus,
    queryClient,
    statusUpdatedAt,
    finishUnconfirmedAttempt,
    finishConnectAttempt,
    unlockOauthLaunch,
  ]);

  useEffect(() => {
    if (isSetupInProgress) {
      clearConnectRecovery();
      unlockOauthLaunch();
    }
  }, [isSetupInProgress, clearConnectRecovery, unlockOauthLaunch]);

  // Fire once per real non-connected → connected transition. The undefined guard keeps the loading-state 'not_started' fallback from seeding the ref, else a cold load straight to connected logs a false completion.
  const priorIntegrationStatusRef = useRef<WhatsAppSetupStatusResponse['status'] | null>(null);
  useEffect(() => {
    if (statusData === undefined) return;
    const prior = priorIntegrationStatusRef.current;
    if (prior && prior !== 'connected' && integrationStatus === 'connected') {
      clearConnectRecovery();
      unlockOauthLaunch();
      closeTimedOutPopup();
      capture(PostHogEventEnum.WhatsappConnectCompleted, { prior_status: prior });
      if (!redirectSuccessPendingRef.current) {
        const awaitedConfirmation = setupConfirmationStatusUpdateCountRef.current !== null;
        setupConfirmationStatusUpdateCountRef.current = null;
        const settledTrackedAttempt = finishConnectAttempt('connected', null, 'status_poll', prior);
        const untrackedPopupSetup = untrackedPopupSetupRef.current;
        untrackedPopupSetupRef.current = null;
        if (!settledTrackedAttempt && untrackedPopupSetup) {
          emitSignupOutcome('connected', null, 'status_poll', {
            ...untrackedPopupSetup,
            priorStatus: prior,
          });
        }
        // The effect that normally clears the flag can't re-run once this guard stops blocking it.
        if (awaitedConfirmation) clearSetupInProgress();
      }
    }
    priorIntegrationStatusRef.current = integrationStatus;
  }, [
    statusData,
    integrationStatus,
    capture,
    clearConnectRecovery,
    unlockOauthLaunch,
    closeTimedOutPopup,
    finishConnectAttempt,
    emitSignupOutcome,
    clearSetupInProgress,
  ]);

  useEffect(() => {
    const initialStatusUpdateCount = setupConfirmationStatusUpdateCountRef.current;
    if (initialStatusUpdateCount === null || redirectSuccessPendingRef.current || statusFetching) {
      return;
    }
    // A terminal failure is a verdict, not an absence of one: settle on it rather than
    // waiting out the cap and reporting a known failure as an inconclusive timeout.
    const failureReason = STATUS_FAILURE_REASONS[integrationStatus];
    if (integrationStatus !== 'connected' && failureReason === undefined) return;
    const statusUpdateCount =
      queryClient.getQueryState([QueryKey.whatsappPhoneNumbers.status])?.dataUpdateCount ?? 0;
    if (statusUpdateCount <= initialStatusUpdateCount) return;
    setupConfirmationStatusUpdateCountRef.current = null;
    const status = failureReason === undefined ? 'connected' : 'failed';
    const reason = failureReason ?? null;
    const settledTrackedAttempt = finishConnectAttempt(status, reason, 'status_poll');
    const untrackedPopupSetup = untrackedPopupSetupRef.current;
    untrackedPopupSetupRef.current = null;
    if (!settledTrackedAttempt && untrackedPopupSetup) {
      emitSignupOutcome(status, reason, 'status_poll', untrackedPopupSetup);
    }
    clearSetupInProgress();
  }, [
    integrationStatus,
    statusFetching,
    statusUpdatedAt,
    queryClient,
    finishConnectAttempt,
    emitSignupOutcome,
    clearSetupInProgress,
  ]);

  useEffect(() => {
    if (
      integrationStatus !== 'connected' ||
      oauthPopupDeadline === null ||
      oauthPopup === null ||
      oauthPopup.initialStatus === 'connected' ||
      oauthNoResultHandledRef.current
    ) {
      return;
    }
    try {
      if (typeof oauthPopup.win.close === 'function') oauthPopup.win.close();
    } catch {
      // COOP can expose an opaque WindowProxy that rejects close after navigation.
    }
    oauthNoResultHandledRef.current = true;
    capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
      had_auth_response: false,
      status: 'connected',
      raw_fb_status: null,
      backend_status: 'connected',
      reason_code: null,
      source: 'status_poll',
      resolution: 'status_poll',
      dialog: oauthPopup.dialog,
      dwell_ms: Date.now() - oauthPopup.openedAt,
    });
    activePopupRef.current = null;
    setOauthPopup(null);
    setOauthPopupDeadline(null);
    unlockOauthLaunch();
  }, [capture, integrationStatus, oauthPopupDeadline, oauthPopup, unlockOauthLaunch]);

  const finishPopupWithoutResult = useCallback(
    (popup: OAuthPopup, resolution: 'popup_closed' | 'popup_deadline' = 'popup_closed') => {
      if (oauthPostMessageReceivedRef.current || oauthNoResultHandledRef.current) return;
      const timedOut = resolution === 'popup_deadline';
      if (!timedOut) {
        try {
          if (typeof popup.win.close === 'function') popup.win.close();
        } catch {
          return;
        }
        if (!popup.win.closed) return;
      }
      oauthNoResultHandledRef.current = true;
      activePopupRef.current = null;
      timedOutPopupRef.current = timedOut ? popup : null;
      capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
        had_auth_response: false,
        status: 'popup_closed_without_redirect',
        raw_fb_status: null,
        backend_status: null,
        reason_code: null,
        source: 'popup_closed',
        resolution,
        dialog: popup.dialog,
        dwell_ms: Date.now() - popup.openedAt,
      });
      confirmConnectRecovery(
        {
          reason: 'popup_closed_without_result',
          dialog: popup.dialog,
          resolution,
        },
        popup.initialStatus,
      );
      setOauthPopup(null);
      setOauthPopupDeadline(null);
    },
    [capture, confirmConnectRecovery],
  );

  // Poll for OAuth popup close
  useEffect(() => {
    if (!oauthPopup) return;
    const { win } = oauthPopup;

    const interval = setInterval(() => {
      if (win.closed && document.hasFocus()) {
        clearInterval(interval);
        finishPopupWithoutResult(oauthPopup);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [oauthPopup, finishPopupWithoutResult]);

  useEffect(() => {
    if (oauthPopupDeadline === null) return;
    const remaining = oauthPopupDeadline - Date.now();
    if (remaining <= 0) {
      setOauthPopupDeadline(null);
      if (oauthPopup) finishPopupWithoutResult(oauthPopup, 'popup_deadline');
      return;
    }
    const stop = setTimeout(() => {
      setOauthPopupDeadline(null);
      if (oauthPopup) finishPopupWithoutResult(oauthPopup, 'popup_deadline');
    }, remaining);
    return () => clearTimeout(stop);
  }, [oauthPopupDeadline, oauthPopup, finishPopupWithoutResult]);

  // Listen for postMessage from the OAuth popup. The popup redirects through
  // the backend, which renders `meta_callback.html`; it posts a
  // `whatsapp_connection_result` message carrying `status` and the
  // backend-emitted `reason_code`. Forward both to the
  // `whatsapp_signup_popup_returned` PostHog event so dashboards stop
  // collapsing everything into `status=unknown`.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Meta's dialog progress. Recorded onto the next signup_result and nothing else: it must
      // never settle an attempt, because a spoofable third-party origin deciding we are
      // "connected" is the vulnerability the check below exists to stop. Scoped to the live
      // popup like the callback branch, so a late post from an abandoned one cannot be
      // attributed to the retry that replaced it.
      if (event.origin === META_DIALOG_ORIGIN) {
        // If Meta turns out to emit from a frame nested inside the popup rather than the
        // popup itself, this check is what will keep all three props null — loosen it here
        // before concluding the dialog says nothing.
        const dialogPopup = activePopupRef.current;
        if (dialogPopup && event.source !== dialogPopup.win) return;
        const signal = parseMetaDialogSignal(event.data);
        if (signal) metaDialogSignalRef.current = signal;
        return;
      }
      // The callback page posts with targetOrigin '*'; without this check any
      // window able to post to us could spoof connect results into the UI.
      if (event.origin !== META_CALLBACK_ORIGIN) return;
      const timedOutPopup = timedOutPopupRef.current;
      const messagePopup = activePopupRef.current ?? timedOutPopup;
      if (messagePopup && event.source !== messagePopup.win) return;
      if (!isWhatsAppConnectionResultMessage(event.data)) return;
      if (oauthPostMessageReceivedRef.current) return;
      if (oauthNoResultHandledRef.current && event.source !== timedOutPopup?.win) return;

      const { status, title, message, reason_code } = event.data;

      // Flag that the popup posted back a structured result, so the
      // popup-close watcher above doesn't double-count this as a silent close.
      oauthPostMessageReceivedRef.current = true;

      capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
        had_auth_response: true,
        status:
          reason_code === 'setup_in_progress'
            ? 'setup_in_progress'
            : status === 'success'
              ? 'connected'
              : reason_code === 'user_cancelled'
                ? 'cancelled'
                : 'backend_error',
        raw_fb_status: null,
        backend_status: status,
        reason_code: reason_code ?? null,
        source: 'popup_postmessage',
        resolution: 'popup_postmessage',
        dialog: messagePopup?.dialog ?? null,
        dwell_ms: messagePopup ? Date.now() - messagePopup.openedAt : null,
      });
      const resultContext: SignupResultContext = {
        dialog: messagePopup?.dialog ?? null,
        startedAt: messagePopup?.openedAt ?? null,
        priorStatus: messagePopup?.initialStatus ?? null,
      };
      const trackedAttempt = connectAttemptRef.current !== null;
      const shouldEmitUntrackedOutcome = !trackedAttempt && messagePopup === null;

      if (timedOutPopup && event.source === timedOutPopup.win) timedOutPopupRef.current = null;
      activePopupRef.current = null;
      clearConnectRecovery();
      setOauthPopup(null);
      setOauthPopupDeadline(null);
      unlockOauthLaunch();

      if (status === 'success') {
        if (reason_code === 'setup_in_progress') {
          setupConfirmationStatusUpdateCountRef.current =
            queryClient.getQueryState([QueryKey.whatsappPhoneNumbers.status])?.dataUpdateCount ?? 0;
          if (shouldEmitUntrackedOutcome) untrackedPopupSetupRef.current = resultContext;
        } else {
          setupConfirmationStatusUpdateCountRef.current = null;
          untrackedPopupSetupRef.current = null;
          if (
            !finishConnectAttempt('connected', null, 'popup_postmessage') &&
            shouldEmitUntrackedOutcome
          ) {
            emitSignupOutcome('connected', null, 'popup_postmessage', resultContext);
          }
        }
        // Covers fresh success, 36009-replay-reconnected, and
        // 36009-replay-setup-in-progress. Localized copy, not the backend's
        // English `title` — the banner below carries the same message.
        markSetupInProgress();
        toast.success(t('whatsapp_phone_numbers.setup.in_progress_title'));
      } else if (reason_code === 'user_cancelled') {
        // Deliberate cancel on Meta's dialog — abandonment, not a failure:
        // no toast, and no unified `failed` so a retry starts a fresh funnel entry.
        setupConfirmationStatusUpdateCountRef.current = null;
        untrackedPopupSetupRef.current = null;
        if (
          !finishConnectAttempt('cancelled', 'user_cancelled', 'popup_postmessage') &&
          shouldEmitUntrackedOutcome
        ) {
          emitSignupOutcome('cancelled', 'user_cancelled', 'popup_postmessage', resultContext);
        }
      } else if (reason_code) {
        // Prefer the structured reason_code mapping over the inline HTML
        // message so every failure mode renders its actionable copy.
        clearSetupInProgress();
        setupConfirmationStatusUpdateCountRef.current = null;
        untrackedPopupSetupRef.current = null;
        if (
          !finishConnectAttempt('failed', reason_code, 'popup_postmessage') &&
          shouldEmitUntrackedOutcome
        ) {
          emitSignupOutcome('failed', reason_code, 'popup_postmessage', resultContext);
        }
        dispatchToastForReasonCode(reason_code);
      } else {
        clearSetupInProgress();
        setupConfirmationStatusUpdateCountRef.current = null;
        untrackedPopupSetupRef.current = null;
        if (
          !finishConnectAttempt('failed', 'backend_error', 'popup_postmessage') &&
          shouldEmitUntrackedOutcome
        ) {
          emitSignupOutcome('failed', 'backend_error', 'popup_postmessage', resultContext);
        }
        const plainMessage = message
          .replace(/<[^>]*>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        toast.error(title, { description: plainMessage, duration: 10000 });
      }
      handleRefreshStatus();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    t,
    handleRefreshStatus,
    capture,
    dispatchToastForReasonCode,
    markSetupInProgress,
    clearSetupInProgress,
    finishConnectAttempt,
    emitSignupOutcome,
    clearConnectRecovery,
    unlockOauthLaunch,
    queryClient,
  ]);

  useEffect(() => {
    if (!redirectSuccessPendingRef.current || integrationStatus !== 'connected') return;
    const alreadySettled = redirectSuccessSettledRef.current;
    if (!alreadySettled) {
      const initialStatusUpdateCount = setupConfirmationStatusUpdateCountRef.current;
      const statusUpdateCount =
        queryClient.getQueryState([QueryKey.whatsappPhoneNumbers.status])?.dataUpdateCount ?? 0;
      if (
        statusFetching ||
        initialStatusUpdateCount === null ||
        statusUpdateCount <= initialStatusUpdateCount
      ) {
        return;
      }
    }
    redirectSuccessPendingRef.current = false;
    redirectSuccessSettledRef.current = false;
    setupConfirmationStatusUpdateCountRef.current = null;
    const dialog = redirectSuccessDialogRef.current;
    redirectSuccessDialogRef.current = null;
    clearSetupInProgress();
    if (!alreadySettled) {
      if (!finishConnectAttempt('connected', null, 'redirect_return')) {
        emitSignupOutcome('connected', null, 'redirect_return', { dialog, startedAt: null });
      }
    }
    toast.success(t('whatsapp_phone_numbers.setup.redirect_return_success'));
  }, [
    integrationStatus,
    statusFetching,
    statusUpdatedAt,
    queryClient,
    clearSetupInProgress,
    emitSignupOutcome,
    finishConnectAttempt,
    t,
  ]);

  const handleOAuthRedirectResult = useCallback(
    ({ status, reason, searchParams }: OAuthRedirectCallbackResult) => {
      const dialog =
        searchParams.get('whatsapp_dialog') === 'permissions' ? 'permissions' : 'embedded_signup';
      capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
        had_auth_response: true,
        status:
          reason === 'user_cancelled'
            ? 'cancelled'
            : reason === 'setup_in_progress'
              ? 'setup_in_progress'
              : status === 'connected'
                ? 'connected'
                : 'backend_error',
        raw_fb_status: null,
        backend_status: status,
        reason_code: reason,
        source: 'redirect_return',
        resolution: 'redirect_return',
        dialog,
        dwell_ms: null,
      });
      if (status === 'connected') {
        redirectSuccessPendingRef.current = true;
        redirectSuccessSettledRef.current = reason !== 'setup_in_progress';
        redirectSuccessDialogRef.current = dialog;
        setupConfirmationStatusUpdateCountRef.current = redirectSuccessSettledRef.current
          ? null
          : (queryClient.getQueryState([QueryKey.whatsappPhoneNumbers.status])?.dataUpdateCount ??
            0);
        if (redirectSuccessSettledRef.current) {
          if (!finishConnectAttempt('connected', null, 'redirect_return')) {
            emitSignupOutcome('connected', null, 'redirect_return', { dialog, startedAt: null });
          }
        }
        markSetupInProgress();
        toast.success(t('whatsapp_phone_numbers.setup.in_progress_title'));
      } else if (reason === 'user_cancelled') {
        setupConfirmationStatusUpdateCountRef.current = null;
        if (!finishConnectAttempt('cancelled', 'user_cancelled', 'redirect_return')) {
          emitSignupOutcome('cancelled', 'user_cancelled', 'redirect_return', {
            dialog,
            startedAt: null,
          });
        }
      } else {
        clearSetupInProgress();
        setupConfirmationStatusUpdateCountRef.current = null;
        if (!finishConnectAttempt('failed', reason ?? 'unknown', 'redirect_return')) {
          emitSignupOutcome('failed', reason ?? 'unknown', 'redirect_return', {
            dialog,
            startedAt: null,
          });
        }
        if (reason) dispatchToastForReasonCode(reason);
        else toast.error(t('whatsapp_phone_numbers.toast.generic.title'));
      }
    },
    [
      t,
      capture,
      markSetupInProgress,
      clearSetupInProgress,
      dispatchToastForReasonCode,
      emitSignupOutcome,
      finishConnectAttempt,
      queryClient,
    ],
  );
  const handleOAuthRedirectComplete = useCallback(() => {
    if (!useModalStore.getState().isOpen(WHATSAPP_PHONE_NUMBERS_SHEET)) {
      toggleModal(WHATSAPP_PHONE_NUMBERS_SHEET);
    }
    handleRefreshStatus();
  }, [toggleModal, handleRefreshStatus]);
  useOAuthRedirectCallback(
    'whatsapp_integration_status',
    handleOAuthRedirectResult,
    WHATSAPP_OAUTH_CALLBACK_PARAMS,
    handleOAuthRedirectComplete,
  );

  const handleClose = () => {
    setDisconnectDialogOpen(false);
    closeModal();
  };

  // Failed-state removals never went live, so the connected-state disconnect copy ("stop receiving messages") is wrong.
  const isStartOverDisconnect =
    integrationStatus === 'setup_incomplete' || integrationStatus === 'waba_inaccessible';
  const disconnectDialogCopy = isStartOverDisconnect
    ? {
        title: t('whatsapp_phone_numbers.disconnect.start_over_title'),
        description: t('whatsapp_phone_numbers.disconnect.start_over_description'),
        confirm: t('whatsapp_phone_numbers.disconnect.start_over_confirm'),
      }
    : {
        title: t('whatsapp_phone_numbers.disconnect.title'),
        description: t('whatsapp_phone_numbers.disconnect.description'),
        confirm: t('whatsapp_phone_numbers.disconnect.confirm'),
      };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        tabIndex={undefined}
        data-testid="whatsapp-phone-numbers-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleClose();
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <WhatsAppIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle>{t('whatsapp_phone_numbers.title')}</SheetTitle>
                <IntegrationStatusBadge connected={integrationStatus === 'connected'} />
                <IntegrationEnabledBadge enabled={phoneNumbers.some((entry) => entry.is_active)} />
              </div>
            </div>
          </div>
          <section className="flex items-center gap-2">
            <IconButton
              onClick={handleClose}
              variant="secondary"
              data-testid="close-button"
              className="shrink-0"
            >
              <CloseIcon />
            </IconButton>
          </section>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('whatsapp_phone_numbers.overview')}
            docsHref={WHATSAPP_DOCS_HREF}
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="whatsapp-connect-enable-section"
            action={
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={handleRefreshStatus}
                      disabled={statusFetching || isPreloading}
                      data-testid="setup-refresh-status-button"
                      aria-label={t('whatsapp_phone_numbers.setup.refresh_status')}
                      // Stay mounted (space reserved) but hidden while preloading so the
                      // section title doesn't shift when the button reappears.
                      className={isPreloading ? 'invisible' : undefined}
                    >
                      <RefreshCw
                        className={`size-4 text-foreground ${statusFetching ? 'animate-spin' : ''}`}
                      />
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('whatsapp_phone_numbers.setup.refresh_status')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            }
          >
            {pendingRecovery && !isSetupInProgress && (
              <div
                className="flex items-center gap-2 py-2 text-muted-foreground"
                role="status"
                aria-live="polite"
                data-testid="whatsapp-connect-confirming-status"
              >
                <Spinner className="size-4" />
                <span className="text-sm">{t('whatsapp_phone_numbers.setup.checking_status')}</span>
              </div>
            )}

            {connectRecovery && !isSetupInProgress && (
              <ConnectRecoveryPanel
                reason={connectRecovery.reason}
                onRetry={() => launchOAuthPopup(connectRecovery.dialog)}
                onContinueInTab={handleContinueInTabClick}
                onDismiss={handleDismissRecovery}
                onReportCause={handleReportRecoveryCause}
                retryDisabled={popupLaunchDisabled}
                continueDisabled={redirectLaunchDisabled}
                docsHref={WHATSAPP_DOCS_HREF}
              />
            )}

            {statusLoading ? (
              <div
                className="flex items-center justify-center gap-2 py-12 text-muted-foreground"
                data-testid="whatsapp-checking-connection"
              >
                <Spinner className="size-5" />
                <span className="text-sm">{t('whatsapp_phone_numbers.setup.checking_status')}</span>
              </div>
            ) : integrationStatus === 'connected' ? null : (
              <div className="flex flex-col gap-2" data-testid="whatsapp-setup-view">
                {integrationStatus === 'waba_inaccessible' && (
                  <Alert variant="warning" data-testid="waba-inaccessible-banner">
                    <AlertTriangle className="size-4" />
                    <AlertTitle>
                      {t('whatsapp_phone_numbers.setup.waba_inaccessible_title')}
                    </AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p className="text-xs">
                        {t('whatsapp_phone_numbers.setup.waba_inaccessible_lead')}
                      </p>
                      <ul className={ALERT_LIST}>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.waba_inaccessible_cause_removed"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.waba_inaccessible_cause_revoked"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                      </ul>
                      <p className="text-xs pt-1 font-medium">
                        {t('whatsapp_phone_numbers.setup.waba_inaccessible_action_label')}
                      </p>
                      <ul className={ALERT_LIST}>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.waba_inaccessible_action_step1"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.waba_inaccessible_action_step2"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                      </ul>
                    </AlertDescription>
                    <div className="flex gap-2 pt-3 !pl-0">
                      <Button
                        onClick={handleConnectClick}
                        size="sm"
                        variant="secondary"
                        className="flex-1"
                        disabled={popupLaunchDisabled || connectRecovery !== null}
                        data-testid="waba-inaccessible-reconnect-button"
                      >
                        <RefreshCw className="size-4" />
                        {t('whatsapp_phone_numbers.setup.waba_inaccessible_reconnect_button')}
                      </Button>
                      <Button
                        onClick={() => setDisconnectDialogOpen(true)}
                        size="sm"
                        variant="danger"
                        className="flex-1"
                        data-testid="waba-inaccessible-remove-button"
                      >
                        <Unplug className="size-4" />
                        {t('integrations:disconnect')}
                      </Button>
                    </div>
                  </Alert>
                )}

                {integrationStatus === 'setup_incomplete' && (
                  <Alert variant="warning" data-testid="setup-incomplete-banner">
                    <AlertTriangle className="size-4" />
                    <AlertTitle>
                      {t('whatsapp_phone_numbers.setup.setup_incomplete_title')}
                    </AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p className="text-xs">
                        {t('whatsapp_phone_numbers.setup.setup_incomplete_lead')}
                      </p>
                      <ul className={ALERT_LIST}>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.setup_incomplete_cause_consent"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.setup_incomplete_cause_other_provider"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                      </ul>
                      <p className="text-xs pt-1 font-medium">
                        {t('whatsapp_phone_numbers.setup.setup_incomplete_action_label')}
                      </p>
                      <ul className={ALERT_LIST}>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.setup_incomplete_action_step1"
                            components={[BOLD_LEAD]}
                          />
                        </li>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="whatsapp_phone_numbers.setup.setup_incomplete_action_step2"
                            components={[BOLD_LEAD, META_BUSINESS_SETTINGS_LINK]}
                          />
                        </li>
                      </ul>
                    </AlertDescription>
                    <div className={ALERT_ACTIONS}>
                      <Button
                        onClick={handleReconnectClick}
                        size="sm"
                        variant="dark"
                        disabled={popupLaunchDisabled || connectRecovery !== null}
                        data-testid="setup-incomplete-reconnect-button"
                      >
                        {t('whatsapp_phone_numbers.setup.setup_incomplete_reconnect_button')}
                      </Button>
                      <Button
                        onClick={() => setDisconnectDialogOpen(true)}
                        size="sm"
                        variant="link"
                        className="text-warning-foreground px-0"
                        data-testid="setup-incomplete-remove-button"
                      >
                        {t('whatsapp_phone_numbers.setup.start_over_button')}
                      </Button>
                    </div>
                  </Alert>
                )}

                {isSetupInProgress && integrationStatus !== 'connected' && (
                  <Alert variant="info" data-testid="setup-in-progress-banner">
                    <Spinner className="size-4" />
                    <AlertTitle>{t('whatsapp_phone_numbers.setup.in_progress_title')}</AlertTitle>
                    <AlertDescription>
                      {t('whatsapp_phone_numbers.setup.in_progress_description')}
                    </AlertDescription>
                  </Alert>
                )}

                {(integrationStatus === 'not_started' || integrationStatus === 'incomplete') &&
                  !connectRecovery && (
                    <div
                      className="flex flex-col gap-1.5 pb-1"
                      data-testid="whatsapp-connect-prerequisites"
                    >
                      <p className="text-sm font-medium text-foreground">
                        {t('integrations:section.prerequisites')}
                      </p>
                      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-muted-foreground marker:text-foreground-subtle">
                        {(
                          t('whatsapp_phone_numbers.prerequisites', {
                            returnObjects: true,
                          }) as string[]
                        ).map((_, index) => (
                          <li key={index}>
                            <Trans
                              t={t}
                              i18nKey={`whatsapp_phone_numbers.prerequisites.${index}`}
                              components={[BOLD_LEAD]}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {integrationStatus !== 'waba_inaccessible' &&
                  integrationStatus !== 'setup_incomplete' &&
                  !connectRecovery && (
                    // Single CTA: Meta's Embedded Signup wizard covers creating a
                    // new WABA and picking an existing one, and the backend
                    // callback completes the whole setup in one pass. The failed
                    // states above carry their own recovery actions instead.
                    <Button
                      onClick={handleConnectClick}
                      variant="dark"
                      size="sm"
                      className="w-full"
                      disabled={isSetupInProgress || popupLaunchDisabled}
                      data-testid="whatsapp-setup-connect"
                    >
                      <Plug className="size-4" />
                      {t('whatsapp_phone_numbers.setup.connect')}
                    </Button>
                  )}

                {statusData &&
                  integrationStatus !== 'connected' &&
                  integrationStatus !== 'waba_inaccessible' &&
                  integrationStatus !== 'setup_incomplete' && (
                    <IssuesBanner issues={statusData.issues} />
                  )}
              </div>
            )}

            {integrationStatus === 'connected' && (
              <IntegrationConnectionCard
                data-testid="integration-info-banner"
                details={[
                  {
                    label: t('whatsapp_phone_numbers.info.whatsapp_account'),
                    value: integrationInfo ? (
                      integrationInfo.whatsapp_id
                    ) : (
                      <Spinner
                        className="size-3.5 text-foreground-subtle"
                        data-testid="whatsapp-account-loader"
                      />
                    ),
                    'data-testid': 'whatsapp-account-label',
                  },
                  {
                    label: t('whatsapp_phone_numbers.info.connected_since'),
                    value: integrationInfo ? (
                      formatDate(integrationInfo.created_timestamp)
                    ) : (
                      <Spinner
                        className="size-3.5 text-foreground-subtle"
                        data-testid="connected-since-loader"
                      />
                    ),
                    'data-testid': 'connected-since',
                  },
                ]}
                actions={
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={handleConnectClick}
                      disabled={
                        integrationInfoLoading || popupLaunchDisabled || connectRecovery !== null
                      }
                      data-testid="whatsapp-reconnect-button"
                    >
                      <Plug className="size-4" />
                      {t('integrations:reconnect')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      onClick={() => setDisconnectDialogOpen(true)}
                      disabled={integrationInfoLoading}
                      data-testid="disconnect-button"
                    >
                      <Unplug className="size-4" />
                      {t('integrations:disconnect')}
                    </Button>
                  </>
                }
              >
                {!integrationInfo ? (
                  <div className="flex items-center" data-testid="whatsapp-status-loading">
                    <Spinner className="size-4 text-foreground-subtle" />
                  </div>
                ) : (
                  <div className="flex items-center">
                    <TooltipProvider>
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-1.5" data-testid="webhook-status">
                          <span
                            className={`inline-block size-2.5 rounded-full ${integrationInfo?.webhook_subscribed ? 'bg-success' : 'bg-destructive'}`}
                          />
                          <span className="text-sm text-muted-foreground">
                            {integrationInfo?.webhook_subscribed
                              ? t('whatsapp_phone_numbers.info.webhook_active')
                              : t('whatsapp_phone_numbers.info.webhook_inactive')}
                          </span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="size-3.5 text-foreground-subtle cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('whatsapp_phone_numbers.info.webhook_tooltip')}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex items-center gap-1.5" data-testid="admin-status">
                          <span
                            className={`inline-block size-2.5 rounded-full ${integrationInfo?.admin_assigned ? 'bg-success' : 'bg-destructive'}`}
                          />
                          <span className="text-sm text-muted-foreground">
                            {integrationInfo?.admin_assigned
                              ? t('whatsapp_phone_numbers.info.admin_assigned')
                              : t('whatsapp_phone_numbers.info.admin_not_assigned')}
                          </span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="size-3.5 text-foreground-subtle cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('whatsapp_phone_numbers.info.admin_tooltip')}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TooltipProvider>
                  </div>
                )}

                {statusData?.send_error && <SendErrorBanner sendError={statusData.send_error} />}

                {statusData && statusData.issues.length > 0 && (
                  <IssuesBanner issues={statusData.issues} />
                )}
              </IntegrationConnectionCard>
            )}
          </IntegrationSection>

          {phoneNumbers.length > 0 && (
            <IntegrationSection
              title={t('integrations:section.settings')}
              data-testid="whatsapp-settings-section"
            >
              <div className="space-y-3">
                {phoneNumbers.map((entry) => (
                  <Card
                    key={entry.phone_number_id}
                    className="py-4 px-5"
                    data-testid={`phone-number-row-${entry.phone_number_id}`}
                  >
                    <div className="grid grid-cols-[2fr_1fr_1fr_auto] items-center gap-x-8 gap-y-4">
                      <div className="min-w-0">
                        <Label className="text-xs text-foreground-subtle font-normal">
                          {t('whatsapp_phone_numbers.card.phone_number')}
                        </Label>
                        <p className="text-sm font-mono mt-0.5 truncate">
                          {entry.display_phone_number}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <Label className="text-xs text-foreground-subtle font-normal">
                          {t('whatsapp_phone_numbers.card.verified_name')}
                        </Label>
                        <p
                          className="text-sm mt-0.5 truncate"
                          data-testid={`verified-name-${entry.phone_number_id}`}
                        >
                          {entry.verified_name}
                        </p>
                      </div>
                      <div className="flex flex-col items-center">
                        <Label className="text-xs text-foreground-subtle font-normal">
                          {t('whatsapp_phone_numbers.card.quality')}
                        </Label>
                        <div className="mt-0.5">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Badge
                                    variant={
                                      QUALITY_BADGE_VARIANT[entry.quality_rating] ?? 'secondary'
                                    }
                                    className="text-xs cursor-default"
                                    data-testid={`quality-rating-${entry.phone_number_id}`}
                                  >
                                    {t(
                                      QUALITY_DISPLAY_KEY[entry.quality_rating] ??
                                        'whatsapp_phone_numbers.card.quality_unknown',
                                    )}
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t(
                                  QUALITY_TOOLTIP_KEY[entry.quality_rating] ??
                                    'whatsapp_phone_numbers.card.quality_unknown_tooltip',
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Label className="text-xs text-foreground-subtle font-normal">
                          {entry.is_active
                            ? t('integrations:status.enabled')
                            : t('integrations:status.disabled')}
                        </Label>
                        <div className="relative flex items-center">
                          {toggleMutation.isPending &&
                            toggleMutation.variables?.phone_number_id === entry.phone_number_id && (
                              <Spinner
                                className="absolute right-full top-1/2 mr-2 -translate-y-1/2 size-3.5 text-foreground-subtle"
                                data-testid={`toggle-loader-${entry.phone_number_id}`}
                              />
                            )}
                          <Switch
                            checked={entry.is_active}
                            onCheckedChange={(checked) => handleToggle(entry, checked)}
                            disabled={toggleMutation.isPending}
                            size="sm"
                            data-testid={`toggle-${entry.phone_number_id}`}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </IntegrationSection>
          )}
        </div>

        <DialogPrimitive.Root open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
          <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <DialogPrimitive.Content
            className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
            role="alertdialog"
            aria-modal="true"
            data-testid="disconnect-dialog"
          >
            <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <CircleAlert className="inline size-4 bg-red-100 rounded-lg text-red-500 p-2 box-content" />
                  {disconnectDialogCopy.title}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogDescription>{disconnectDialogCopy.description}</AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel asChild>
                  <Button size="md" variant="secondary">
                    {t('whatsapp_phone_numbers.disconnect.cancel')}
                  </Button>
                </AlertDialogCancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  data-testid="disconnect-confirm-button"
                >
                  {deleteMutation.isPending && <Spinner className="size-4 mr-1.5" />}
                  {disconnectDialogCopy.confirm}
                </Button>
              </AlertDialogFooter>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
